import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { IFlowButton, FlowButtonState } from '@/types/flowbutton';
import { FLOW_BUTTON_COLORS } from '@/types/flowbutton';
import { Loader2 } from 'lucide-react';
import { FLOW_HANDLERS } from './handlers';
import PartCreatePipeline from './dialogs/PartCreatePipeline';
import InvoiceProcessPipeline from './dialogs/InvoiceProcessPipeline';
import BatchInvoiceReview from './dialogs/BatchInvoiceReview';
import BatchInvoicePipeline from './dialogs/BatchInvoicePipeline';

const LONG_PRESS_MS = 500;
const POSITION_KEY_PREFIX = 'flowbutton-pos:';

interface FlowButtonRendererProps {
  button: IFlowButton;
  onEdit?: (button: IFlowButton) => void;
  onDelete?: (id: string) => void;
  onDragStart?: (button: IFlowButton) => void;
  onDragEnd?: (button: IFlowButton, pointerX: number, pointerY: number) => void;
  isDragMode?: boolean;
  readOnly?: boolean;
}

const FlowButtonRenderer: React.FC<FlowButtonRendererProps> = ({ button, onEdit, onDelete, onDragStart, onDragEnd, isDragMode, readOnly }) => {
  const [state, setState] = useState<FlowButtonState>({ status: 'idle' });
  const [position, setPosition] = useState<{ x: number; y: number } | null>(() => {
    try {
      const saved = localStorage.getItem(POSITION_KEY_PREFIX + button.id);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [activeDialog, setActiveDialog] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const widgetRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const didDrag = useRef(false);

  const colors = FLOW_BUTTON_COLORS[button.color] || FLOW_BUTTON_COLORS.blue;

  const clampPosition = useCallback((x: number, y: number) => {
    const el = widgetRef.current;
    if (!el) return { x, y };
    return {
      x: Math.max(0, Math.min(window.innerWidth - el.offsetWidth, x)),
      y: Math.max(0, Math.min(window.innerHeight - el.offsetHeight, y)),
    };
  }, []);

  // Save position to localStorage
  useEffect(() => {
    if (position) {
      localStorage.setItem(POSITION_KEY_PREFIX + button.id, JSON.stringify(position));
    }
  }, [position, button.id]);

  // Clamp on resize
  useEffect(() => {
    if (!position) return;
    const handler = () => setPosition(prev => prev ? clampPosition(prev.x, prev.y) : null);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [position, clampPosition]);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0 || readOnly) return;
    didDrag.current = false;

    longPressTimer.current = setTimeout(() => {
      isDragging.current = true;
      const rect = widgetRef.current?.getBoundingClientRect();
      if (rect) {
        dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        setPosition({ x: rect.left, y: rect.top });
      }
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      setDragging(true);
      onDragStart?.(button);
    }, LONG_PRESS_MS);
  }, [readOnly, button, onDragStart]);

  const lastPointer = useRef({ x: 0, y: 0 });

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    lastPointer.current = { x: e.clientX, y: e.clientY };
    // Movement during long-press countdown cancels it (latch until next press)
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (!isDragging.current) return;
    e.preventDefault();
    didDrag.current = true;
    const newPos = clampPosition(e.clientX - dragOffset.current.x, e.clientY - dragOffset.current.y);
    setPosition(newPos);
  }, [clampPosition]);

  const handlePointerUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    const wasDragging = isDragging.current;
    isDragging.current = false;
    setDragging(false);
    if (wasDragging && didDrag.current) {
      onDragEnd?.(button, lastPointer.current.x, lastPointer.current.y);
    }
  }, [button, onDragEnd]);

  const handlePointerCancel = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    const wasDragging = isDragging.current;
    isDragging.current = false;
    setDragging(false);
    if (wasDragging) {
      onDragEnd?.(button, -1, -1);
    }
  }, [button, onDragEnd]);

  const handleClick = useCallback(() => {
    if (didDrag.current) return;
    if (readOnly) return;
    if (state.status === 'idle') {
      setShowMenu(prev => !prev);
    } else if (state.status === 'error') {
      setState({ status: 'idle' });
    }
  }, [state.status, readOnly]);

  const handleMenuAction = useCallback(async (action: () => void | Promise<void>) => {
    setShowMenu(false);
    try {
      await action();
    } catch {
      // handled by flow handler
    }
  }, []);

  const handleExecute = useCallback(async (executeFn: () => Promise<void>) => {
    setShowMenu(false);
    setState({ status: 'active', action: 'running' });
    try {
      await executeFn();
      setState({ status: 'success' });
      setTimeout(() => setState({ status: 'idle' }), 2000);
    } catch (err) {
      setState({ status: 'error', message: err instanceof Error ? err.message : 'Unknown error' });
    }
  }, []);

  const handleOpenDialog = useCallback((dialogId: string) => {
    setShowMenu(false);
    setActiveDialog(dialogId);
  }, []);

  const handleDialogComplete = useCallback(() => {
    setActiveDialog(null);
    setState({ status: 'success' });
    setTimeout(() => setState({ status: 'idle' }), 2000);
  }, []);

  const handleDialogCancel = useCallback(() => {
    setActiveDialog(null);
    setState({ status: 'idle' });
  }, []);

  // Get handler and menu items
  const handler = FLOW_HANDLERS[button.flow_type];
  const menuItems = handler?.getMenuItems?.(button) || [];

  // Build button management items
  const managementItems = [];
  if (onEdit) {
    managementItems.push({ label: 'Edit Button', onClick: () => onEdit(button) });
  }
  if (onDelete) {
    managementItems.push({ label: 'Delete Button', onClick: () => onDelete(button.id), variant: 'danger' as const });
  }

  // Shape classes
  const shapeClass = {
    circle: 'rounded-full w-12 h-12',
    square: 'rounded-md w-12 h-12',
    pill: 'rounded-full px-4 py-2',
    'rounded-rect': 'rounded-lg px-4 py-2',
  }[button.shape] || 'rounded-full px-4 py-2';

  const isActive = state.status === 'active' || state.status === 'locked_elsewhere';
  const isSuccess = state.status === 'success';

  // Dragged button: z-50 (above dark overlay at z-45). Others: z-40 (behind overlay).
  const zIndex = dragging ? 50 : (isDragMode && !dragging) ? 40 : 50;
  const posStyle: React.CSSProperties = position
    ? { position: 'fixed', left: position.x, top: position.y, bottom: 'auto', right: 'auto', zIndex }
    : {};

  return (
    <div
      ref={widgetRef}
      style={posStyle}
      className={`${position ? '' : 'relative'} select-none touch-none`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      {/* The button */}
      <button
        onClick={handleClick}
        disabled={isActive}
        className={`
          ${shapeClass} ${colors.bg} ${colors.text}
          flex items-center justify-center gap-1.5
          shadow-lg hover:shadow-xl transition-all duration-200
          border-2 ${colors.border}
          ${isActive ? 'opacity-75 cursor-not-allowed animate-pulse' : ''}
          ${isSuccess ? 'ring-2 ring-green-400' : ''}
          ${dragging ? 'cursor-grabbing scale-125 ring-4 ring-white/60 shadow-2xl animate-pulse' : 'cursor-pointer'}
          text-sm font-medium whitespace-nowrap
        `}
        style={dragging ? { filter: 'brightness(1.2) drop-shadow(0 0 12px rgba(255,255,255,0.5))' } : undefined}
      >
        {isActive && <Loader2 className="w-4 h-4 animate-spin" />}
        {isSuccess && (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
        <span className="text-xs">{button.name}</span>
      </button>

      {/* Subtitle */}
      {button.subtitle && (button.shape === 'pill' || button.shape === 'rounded-rect') && (
        <div className="text-center mt-0.5">
          <span className="text-[10px] text-gray-400">{button.subtitle}</span>
        </div>
      )}

      {/* Action menu */}
      {showMenu && (
        <div className="absolute bottom-full mb-2 right-0 w-52 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-[60]">
          {menuItems.map((item, i) => (
            <div key={i} className="relative group/submenu">
              <button
                onClick={() => {
                  if (item.children) return; // submenu parent — hover only
                  if (item.navigateTo) {
                    setShowMenu(false);
                    navigate(item.navigateTo);
                  } else if (item.openDialog) {
                    handleOpenDialog(item.openDialog);
                  } else if (item.execute) {
                    handleExecute(item.execute);
                  } else {
                    handleMenuAction(item.onClick);
                  }
                }}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                {item.icon && <span className="text-base">{item.icon}</span>}
                <span className="flex-1">{item.label}</span>
                {item.children && <span className="text-gray-400 text-xs">▸</span>}
              </button>

              {/* Submenu flyout — extends to the right */}
              {item.children && item.children.length > 0 && (
                <div className="hidden group-hover/submenu:block absolute left-full top-0 ml-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-[61] max-h-80 overflow-y-auto">
                  {item.children.map((child, j) => (
                    <button
                      key={j}
                      onClick={() => {
                        if (child.navigateTo) {
                          setShowMenu(false);
                          navigate(child.navigateTo);
                        } else if (child.openDialog) {
                          handleOpenDialog(child.openDialog);
                        } else {
                          handleMenuAction(child.onClick);
                        }
                      }}
                      disabled={!child.navigateTo && !child.openDialog && !child.onClick}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-default"
                    >
                      {child.icon && <span className="text-base">{child.icon}</span>}
                      <span className="truncate">{child.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {managementItems.length > 0 && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              {managementItems.map((item, i) => (
                <button
                  key={`mgmt-${i}`}
                  onClick={() => handleMenuAction(item.onClick)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                    item.variant === 'danger'
                      ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {/* Dialog pipelines */}
      {activeDialog === 'part-create-pipeline' && (
        <PartCreatePipeline
          onComplete={handleDialogComplete}
          onCancel={handleDialogCancel}
        />
      )}
      {activeDialog === 'invoice-process-pipeline' && (
        <InvoiceProcessPipeline
          onComplete={handleDialogComplete}
          onCancel={handleDialogCancel}
        />
      )}
      {activeDialog === 'batch-invoice-review' && (
        <BatchInvoiceReview
          onComplete={handleDialogComplete}
          onCancel={handleDialogCancel}
        />
      )}
      {activeDialog === 'batch-invoice-pipeline' && (
        <BatchInvoicePipeline
          onComplete={handleDialogComplete}
          onCancel={handleDialogCancel}
        />
      )}
      {/* PO flows are now URL-routed via /app/psi/purchases?flow=... */}
      {false && activeDialog?.startsWith('purchase-order-agentic') && (
        <div />
      )}
    </div>
  );
};

export default FlowButtonRenderer;
